import { Box, Button, FormControl, Grid, IconButton, InputAdornment, LinearProgress, MenuItem, Select, TextField, Typography } from '@mui/material';
import { TEXT_LIGHT_COLOR, TEXT_TITLE_COLOR } from '../../constants/colors';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import AddIcon from '@mui/icons-material/Add';
import ErrorMessageComponent from '../../components/errorMessage';
import ConfirmDialog from '../../components/ConfirmDialog';
import { BannerModel } from '../../models/banner';
import { BannerEnum, BannerSlideEnum } from '../../enums/bannerEnum';
import { convertBannerTypeToString } from '../../utils/convertBannerTypeToString';
import { useTranslation } from 'react-i18next';
import { useBannerFormManageControllers } from './controller/form';
import { MerChantModel } from '../../models/merChantModel';
import i18next from 'i18next';
import { EN_ACCEPT_LANGUAGE } from '../../constants/acceptLanguageApi';
import { MainGroupModel } from '../../models/mainGroup';
import { DivisionModel } from '../../models/division';
import { CategoriesModel } from '../../models/categories';
import SearchIcon from '@mui/icons-material/Search';
import { ProductMerchantModel } from '../../models/productMerchant';
import { SubCategoryModel } from '../../models/subCategory';
import { getLanguageLabel } from '../../utils/getLanguageLabel';
import { TagPromotionModel } from '../../models/tagPromotion.model';

interface Props {
  onClose: VoidFunction;
  bannerType: BannerEnum;
  selectedData: BannerModel,
  onGetData: any,
  branchData: MerChantModel[]
  tagData: TagPromotionModel[]
}

const FormBannerManageView = ({ onClose, bannerType, selectedData, onGetData, branchData, tagData }: Props) => {
  const controller = useBannerFormManageControllers(
    {
      selectedData: selectedData,
      onClose: onClose,
      onGetData,
      bannerType
    }
  );
  const isDisabled = !!selectedData;

  const renderCategorySection = () => {

    if (controller.loadingCategories) return <LinearProgress />;
    if (bannerType === BannerEnum.COUPON || bannerType === BannerEnum.PROMOTION) return null;
    if (selectedData?.subCategoryId === null) return null

    return (
      <>
        {
          controller.mainGroupData?.length > 0 &&
          <Grid item xs={6}>
            <Typography>{t("banner_page.Main Group")}</Typography>
            <FormControl fullWidth margin='normal'>
              <Select
                size='small'
                id="select-main-group-id"
                value={controller.selectedMainCategory}
                onChange={controller.handleSelectMainGroup}
                disabled={isDisabled}
              >
                {
                  controller.mainGroupData?.map((item: MainGroupModel) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      {i18next.language === EN_ACCEPT_LANGUAGE ? item?.supplies_main_group_name?.main_group_name_en : item?.supplies_main_group_name?.main_group_name_la}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        }
        {
          controller.divisionData?.length > 0 &&
          <Grid item xs={6}>
            <Typography>{t("banner_page.Division")}</Typography>
            <FormControl fullWidth margin='normal'>
              <Select
                size='small'
                id="select-division-id"
                value={controller.selectedDivision}
                onChange={controller.handleSelectDivision}
                disabled={isDisabled}
              >
                {
                  controller.divisionData?.map((item: DivisionModel) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      {i18next.language === EN_ACCEPT_LANGUAGE ? item?.supplies_division_name?.division_name_en : item?.supplies_division_name?.division_name_la}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        }
        {
          controller.categoriesData?.length > 0 &&
          <Grid item xs={6}>
            <Typography>{t("banner_page.Categories")}</Typography>
            <FormControl fullWidth margin='normal'>
              <Select
                size='small'
                id="select-division-id"
                value={controller.selectedCategories}
                onChange={controller.handleSelectCategories}
                disabled={isDisabled}
              >
                {
                  controller.categoriesData?.map((item: CategoriesModel) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      {i18next.language === EN_ACCEPT_LANGUAGE ? item?.supplies_product_category?.category_name_en : item?.supplies_product_category?.category_name_la}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        }
        {
          controller.subCategoryData?.length > 0 &&
          <Grid item xs={6}>
            <Typography>{t("banner_page.Sub Categories")}</Typography>
            <FormControl fullWidth margin='normal'>
              <Select
                size='small'
                id="select-division-id"
                value={controller.selectedSubCategory}
                onChange={controller.handleSelectSubCategory}
                disabled={isDisabled}
              >
                {
                  controller.subCategoryData?.map((item: SubCategoryModel) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      {getLanguageLabel(item?.supplies_product_sub_category?.sub_category_name_en, item?.supplies_product_sub_category?.sub_category_name_la)}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        }

      </>
    )
  }

  const renderProductSearchSection = () => {

    return (
      <>
        {/* Search product */}
        {controller.selectedTypeBanner === BannerSlideEnum.PRODUCT_DETAIL && (
          <Grid item xs={6}>
            <Typography>{t("banner_page.Search Product")}</Typography>
            <TextField
              fullWidth size="small" margin="normal"
              inputRef={controller.itemCodeRef}
              disabled={isDisabled}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={controller.handleGetProduct}>
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        )}
        {
          controller.productData?.length > 0 &&
          <Grid item xs={6}>
            <Typography>{t("banner_page.Select Product")}</Typography>
            <FormControl fullWidth margin='normal'>
              <Select
                size='small'
                id="select-product-id"
                value={controller.productValue}
                onChange={controller.handleSelectProduct}
                disabled={isDisabled}
              >
                {
                  controller.productData?.map((item: ProductMerchantModel) => (
                    <MenuItem key={item?.id} value={item?.id}>
                      {i18next.language === EN_ACCEPT_LANGUAGE ? item?.price?.masterProduct?.productNameEn : item?.price?.masterProduct?.productNameLa}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </Grid>
        }
      </>
    )
  }

  const renderTagPromotionSection = () => {

    return (
      <>
        <Grid item xs={6}>
          <Typography>{t("banner_page.Select Tag Promotion")}</Typography>
          <FormControl fullWidth margin='normal'>
            <Select
              size='small'
              id="select-tag-pro-id"
              value={controller.selectTagPromotion}
              onChange={controller.handleSelectTagPromotion}
              disabled={isDisabled}
            >
              {
                tagData.map((item: TagPromotionModel) => (
                  <MenuItem key={item?.id} value={item?.id}>
                    {item?.tagName}
                  </MenuItem>
                ))
              }
            </Select>
          </FormControl>
        </Grid>
      </>
    )
  }

  const FormSection = () => {
    switch (controller.selectedTypeBanner) {
      case BannerSlideEnum.CATEGORY:
        return renderCategorySection();
      case BannerSlideEnum.PRODUCT_DETAIL:
        return renderProductSearchSection();
      case BannerSlideEnum.PROMOTION_FLASH_SALE:
        return renderTagPromotionSection();
      default:
        return null;
    }
  };

  const FormPromotionAndCouponSection = () => {
    switch (bannerType) {
      case BannerEnum.PROMOTION:
        return renderTagPromotionSection()
      default:
        return null;
    }
  }

  const { t } = useTranslation("global");

  return (
    <Box>
      <IconButton onClick={onClose} sx={{ mb: 2 }}>
        <KeyboardBackspaceIcon />
      </IconButton>
      <Typography variant="h4" color={TEXT_TITLE_COLOR} sx={{ mb: 4 }}>
        {selectedData ? t("banner_page.Update Banner") : t("banner_page.Create Banner")}
      </Typography>
      <Box sx={{ maxWidth: 750 }}>
        <form onSubmit={controller.handleSubmit}>
          <Box sx={{ p: 4, boxShadow: '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12)'}} >
            <Typography sx={{ mb: 2, color: 'red' }}><span style={{ color: "black" }}>{t("banner_page.Type")}: </span>{t(`banner_page.${convertBannerTypeToString(bannerType)}`)}</Typography>
            {
              selectedData && <Typography sx={{ mb: 2, color: 'red' }}><span style={{ color: "black" }}>{t("banner_page.Create by")}: </span>{selectedData?.createdBy}</Typography>
            }
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box sx={{ width: '100%' }}>
                  <Typography>{t("banner_page.Start Date")}</Typography>
                  <TextField inputRef={controller.startDateRef} required fullWidth margin='normal' size="small" type="date" />
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ width: '100%' }}>
                  <Typography>{t("banner_page.End Date")}</Typography>
                  <TextField inputRef={controller.endDateRef} required fullWidth margin='normal' size="small" type="date" />
                </Box>
              </Grid>

              {/* Banner Type */}
              {bannerType !== BannerEnum.PROMOTION && bannerType !== BannerEnum.COUPON && (
                <Grid item xs={6}>
                  <Box sx={{ width: '100%' }}>
                    <Typography>{t("banner_page.Select Banner type")}</Typography>
                    <FormControl fullWidth margin="normal">
                      <Select
                        size="small"
                        id="select-banner-type-id"
                        value={controller.selectedTypeBanner}
                        onChange={controller.handleSelectedBannerType}
                        disabled={isDisabled}
                      >
                        {Object.values(BannerSlideEnum).map((value) => (
                          <MenuItem key={value} value={value}>
                            {t(`banner_page.${value}`)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Grid>
              )}

              {/* Select Merchant */}
              <Grid item xs={6}>
                <Box sx={{ width: '100%' }}>
                  <Typography>{t("banner_page.Select Merchant")}</Typography>
                  <FormControl fullWidth margin='normal'>
                    <Select
                      size='small'
                      id="select-merchant-id"
                      value={controller.selectedMerchantValue || ""}
                      onChange={controller.handleSelectedMerchant}
                      disabled={isDisabled}
                    >
                      {
                        branchData?.map((item: MerChantModel) => (
                          <MenuItem key={item?.id} value={item?.id || ''}>
                            {i18next.language === EN_ACCEPT_LANGUAGE ? item?.nameEn : item?.nameLa}
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
              <FormSection />
              <FormPromotionAndCouponSection />
            </Grid>

            <ErrorMessageComponent errorMessage={controller.errorMessage} />
            <Box mt={2}>
              <Button type="submit" variant="contained" sx={{ textTransform: 'none', width: 120, background: '#11A2F1', borderRadius: '10px' }}>
                { selectedData ? t("Update") : t("Create") }
              </Button>
            </Box>
          </Box>
        </form>

      </Box>

      {/* Image */}
      <Box sx={{ maxWidth: !controller.imageToShow ? 750 : null }}>
        <Typography sx={{ mt: 2 }}>{t("banner_page.720 x 270 is suitable for app")}</Typography>
        <Box
          sx={{
            paddingTop: 5,
            paddingBottom: 5,
            mt: 3,
            backgroundColor: '#F4F6F8',
            border: '1px dashed rgba(145, 158, 171, 0.32);',
            borderRadius: '8px'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <label htmlFor="image-input-popup">
              <Box sx={{ cursor: 'pointer' }}>
                {
                  controller.imageToShow
                    ?
                    <img src={controller.imageToShow} alt="" style={{ width: '571px', height: 'auto', maxWidth: '571px', maxHeight: '320px', borderRadius: '16px', objectFit: 'cover' }} />
                    :
                    <Box sx={{ width: 179, height: 164, backgroundColor: '#EBEBEB', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                      <AddIcon sx={{ fontSize: 45, color: '#9C9C9C' }} />
                      <Typography color={TEXT_LIGHT_COLOR}>{t("banner_page.Add image")}</Typography>
                    </Box>
                }

              </Box>
            </label>
            <input
              type="file"
              accept='image/*'
              id="image-input-popup"
              onChange={controller.handleSelectImage}
              onClick={(e: any) => {
                e.target.value = '';
              }}
              style={{ display: 'none' }}
            />
          </Box>
        </Box>
      </Box>
      <Box>
        <ConfirmDialog
          open={controller.openConfirmDialog}
          // title={`Do you want to ${controller.selectedData ? "update" : "add"} data?`}
          title={`${t('popup_page.Do you want to')}${controller.selectedData ? t('banner_page.Update Banner') : t('banner_page.Create Banner')}?`}
          loading={controller.loadingSubmit}
          onCancel={() => controller.handleSetOpenConfirmDialog(false)}
          onConfirm={() => controller.handleConfirmDialog()}
        />
      </Box>
    </Box>
  );
};
export default FormBannerManageView;
